import { useState, useCallback } from 'react';
import { Avatar, Button, Popconfirm, Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import {
  PhoneOutlined, BellFilled, CheckSquareOutlined, CalendarOutlined,
  DeleteOutlined, CheckOutlined, RollbackOutlined, ClockCircleOutlined,
  UserOutlined, FolderOpenOutlined,
} from '@ant-design/icons';
import Moment from 'react-moment';
import styles from './ActivityItem.module.scss';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import ActiviteService from '../../services/ActiviteService';
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import PropTypes from 'prop-types';

/* ── Type config ───────────────────────── */
export const TYPE_CONFIG = {
  Appel:   { icon: <PhoneOutlined />,       color: '#FA8C16', label: 'Appel' },
  Tache:   { icon: <CheckSquareOutlined />, color: 'var(--primary-color, #3E65AC)', label: 'Tâche' },
  Rappel:  { icon: <BellFilled />,          color: '#722ED1', label: 'Rappel' },
  Reunion: { icon: <CalendarOutlined />,    color: '#52C41A', label: 'Réunion' },
};

/* ── Parent link helper ────────────────── */
function ParentChip({ parentable, parentType }) {
  if (!parentable) return null;

  const isAffaire = parentType === 'Affaire';
  const isTache   = parentType === 'Tache';
  const label     = parentable.nom ?? parentable.code ?? `#${parentable.id}`;
  const icon      = isAffaire ? <FolderOpenOutlined /> : <CheckSquareOutlined />;
  const to        = isAffaire
    ? `/affaire/${parentable.uid}`
    : isTache
      ? `/tache/${parentable.uid}`
      : null;

  if (!to) return (
    <span className={styles.parentChip} title={label}>
      {icon}<span className={styles.parentChipText}>{label}</span>
    </span>
  );

  return (
    <NavLink to={to} className={styles.parentChip} title={label} onClick={e => e.stopPropagation()}>
      {icon}<span className={styles.parentChipText}>{label}</span>
    </NavLink>
  );
}

/* ── Date badge helper ─────────────────── */
function DateBadge({ date, isDone, doneAt }) {
  if (isDone && doneAt) {
    return (
      <span className={`${styles.dateBadge} ${styles.doneBadge}`}>
        <CheckOutlined /> <Moment format="DD/MM/YY">{doneAt}</Moment>
      </span>
    );
  }
  if (!date) return null;

  const d       = new Date(date);
  const now     = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isOver  = d < now && !isToday;

  let cls    = styles.upcoming;
  if (isOver)  cls = styles.overdue;
  if (isToday) cls = styles.today;

  return (
    <span className={`${styles.dateBadge} ${cls}`}>
      <ClockCircleOutlined />
      <Moment format="DD/MM/YY">{date}</Moment>
    </span>
  );
}

/* ── Main component ────────────────────── */
const ActivityItem = ({ item, onDelete, onToggleDone, onUpdate, users }) => {
  const [activity, setActivity] = useState(item);
  const [busy, setBusy]         = useState(false);

  const cfg = TYPE_CONFIG[activity.type] ?? TYPE_CONFIG.Rappel;
  const chipBg = cfg.color.startsWith('var(') ? 'rgba(62,101,172,0.10)' : `${cfg.color}18`;

  const handleDelete = () => {
    setBusy(true);
    ActiviteService.getInstance()
      .remove(activity.uid)
      .then(res => { onDelete(activity); Toast.success(res.message); })
      .catch(catchError)
      .finally(() => setBusy(false));
  };

  const handleToggleDone = () => {
    setBusy(true);
    ActiviteService.getInstance()
      .switchDone(activity.uid)
      .then(res => {
        const updated = { ...activity, done: !activity.done, done_at: res.data?.done_at };
        setActivity(updated);
        onToggleDone(updated);
        Toast.success(res.message);
      })
      .catch(catchError)
      .finally(() => setBusy(false));
  };

  const handleFieldChange = useCallback((value, fieldName, record) => {
    const updated = { ...record, [fieldName]: value };
    setActivity(updated);
    onUpdate(updated);
    ActiviteService.getInstance()
      .update(updated.uid, updated)
      .then(res => { setActivity(res.data); onUpdate(res.data); Toast.success(res.message); })
      .catch(catchError);
  }, [onUpdate]);

  return (
    <div className={`${styles.item} ${activity.done ? styles.done : ''}`}>

      {/* Bande colorée gauche */}
      <div className={styles.colorStripe} style={{ background: cfg.color }} />

      {/* Type chip + titre — groupés pour le réagencement mobile */}
      <div className={styles.headerRow}>
        <span
          className={styles.typeChip}
          style={{ background: chipBg, color: cfg.color }}
        >
          {cfg.icon}
          <span className={styles.typeLabel}>{cfg.label}</span>
        </span>

        <div className={styles.nameBlock}>
          <InlineEditableInput
            canEdit
            value={activity.nom}
            record={activity}
            onChange={handleFieldChange}
            fieldName="nom"
          >
            <span className={styles.nameText}>{activity.nom}</span>
          </InlineEditableInput>
        </div>
      </div>

      {/* Parent / date / responsable — groupés pour le réagencement mobile */}
      <div className={styles.metaRow}>
        {activity.parentable && (
          <Tooltip title={activity.parentable.nom}>
            <ParentChip
              parentable={activity.parentable}
              parentType={activity.parent_type}
            />
          </Tooltip>
        )}

        <InlineEditableInput
          canEdit={!activity.done}
          type="datetime-local"
          value={activity.end_at}
          record={activity}
          onChange={handleFieldChange}
          fieldName="end_at"
        >
          <DateBadge
            date={activity.end_at}
            isDone={activity.done}
            doneAt={activity.done_at}
          />
        </InlineEditableInput>

        <span className={styles.assignee}>
          <InlineEditableInput
            canEdit
            type="select"
            value={activity.whoid}
            record={activity}
            onChange={handleFieldChange}
            fieldName="whoid"
            options={users.map(u => ({ value: u.id, label: u.name, children: u.name }))}
          >
            {activity.who
              ? (
                <Tooltip title={activity.who.name}>
                  <Avatar size={22} src={activity.who.avatar} icon={<UserOutlined />} />
                </Tooltip>
              )
              : <Avatar size={22} icon={<UserOutlined />} style={{ background: '#DFE1E6', color: '#8C9BAB' }} />
            }
          </InlineEditableInput>
        </span>
      </div>

      {/* Actions (visibles au hover) */}
      <div className={styles.actions}>
        <Tooltip title={activity.done ? 'Réouvrir' : 'Marquer terminée'}>
          <Popconfirm
            title={activity.done ? 'Réouvrir cette activité ?' : 'Marquer comme terminée ?'}
            onConfirm={handleToggleDone}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              type="text"
              size="small"
              loading={busy}
              icon={activity.done ? <RollbackOutlined /> : <CheckOutlined />}
              className={activity.done ? styles.btnReopen : styles.btnDone}
            />
          </Popconfirm>
        </Tooltip>

        <Tooltip title="Supprimer">
          <Popconfirm
            title="Supprimer cette activité ?"
            onConfirm={handleDelete}
            okText="Supprimer"
            okButtonProps={{ danger: true }}
            cancelText="Annuler"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>
      </div>
    </div>
  );
};

ActivityItem.propTypes = {
  item:         PropTypes.object.isRequired,
  onDelete:     PropTypes.func.isRequired,
  onToggleDone: PropTypes.func.isRequired,
  onUpdate:     PropTypes.func.isRequired,
  users:        PropTypes.array.isRequired,
};

ParentChip.propTypes = {
  parentable: PropTypes.object,
  parentType: PropTypes.string,
};

DateBadge.propTypes = {
  date:    PropTypes.string,
  isDone:  PropTypes.bool,
  doneAt:  PropTypes.string,
};

export default ActivityItem;
